// Typed game-wide event bus. Systems communicate through these events instead
// of reaching into each other. Per-room systems MUST unsubscribe in destroy()
// (use Subscriptions) because GameScene restarts for every room.

import type {
  DamageInfo,
  ItemInstance,
  OrbType,
  RouteNode,
  SignatureId,
  SynergyId,
  UltimateId,
  WeaponId,
} from './types';
import type { BossApi, EnemyApi, ProjectileSpec } from './contracts';

export interface GameEvents {
  // Player
  'player:damaged': { amount: number; hp: number; shield: number; shieldBroken: boolean; info: DamageInfo };
  'player:healed': { amount: number };
  'player:died': { x: number; y: number };
  'player:jump': { air: boolean; x: number; y: number };
  'player:land': { impact: number; x: number; y: number };
  'player:dash': { x: number; y: number; dir: number };
  'player:dashEnd': { x0: number; y0: number; x1: number; y1: number };
  'player:shieldBreak': Record<string, never>;
  'player:shieldRestored': Record<string, never>;
  'player:overheat': { weapon: WeaponId };
  'player:ventEnd': { weapon: WeaponId };
  'player:signature': { id: SignatureId };
  'player:signatureReady': { id: SignatureId };
  'player:ultimate': { id: UltimateId };
  'player:ultimateReady': Record<string, never>;

  // Weapons
  /** specs = every projectile spawned by this trigger pull (echoes replay them). Beam/hitscan weapons pass []. */
  'weapon:fired': { weapon: WeaponId; x: number; y: number; angle: number; specs: ProjectileSpec[]; secondary?: boolean };
  'weapon:switched': { index: number; weapon: WeaponId | null };

  // Enemies / bosses
  'enemy:spawned': { enemy: EnemyApi };
  'enemy:damaged': { enemy: EnemyApi; amount: number; info: DamageInfo; crit: boolean };
  'enemy:killed': { enemy: EnemyApi; x: number; y: number; info: DamageInfo };
  'boss:spawned': { boss: BossApi };
  'boss:phase': { boss: BossApi; phase: number };
  'boss:defeated': { boss: BossApi };

  // Rooms / encounters
  'room:entered': { node: RouteNode; roomId: string };
  'room:combatStart': Record<string, never>;
  'room:waveStart': { wave: number; total: number };
  'room:cleared': { node: RouteNode; timeSec: number; damageTaken: number };
  /** World asks GameScene to leave the room (player used the exit). */
  'room:exitRequested': Record<string, never>;
  /** Player closed the route map without picking; they stay in the room. */
  'room:exitCancelled': Record<string, never>;
  'challenge:started': { id: string; text: string };
  'challenge:completed': { id: string; text: string };
  'challenge:failed': { id: string; text: string };

  // Loot / inventory
  'orb:collected': { type: OrbType; x: number; y: number };
  'loot:revealed': { item: ItemInstance; autoEquipped: boolean };
  'item:equipped': { item: ItemInstance; replaced: ItemInstance | null };
  'item:salvaged': { item: ItemInstance; scrap: number };
  'inventory:changed': Record<string, never>;
  'synergy:activated': { id: SynergyId };
  'synergy:deactivated': { id: SynergyId };
  'scrap:changed': { total: number; delta: number };

  // UI
  'ui:toast': { text: string; color?: number; duration?: number };
  'ui:banner': { title: string; subtitle?: string; color?: number };

  // Run
  'run:sectorComplete': { sector: number };
  'run:ended': { victory: boolean };
  'settings:changed': Record<string, never>;
}

export type EventKey = keyof GameEvents;
type Handler<K extends EventKey> = (payload: GameEvents[K]) => void;

export class EventBus {
  private handlers = new Map<EventKey, Set<Handler<any>>>();

  /** Subscribe; returns an unsubscribe function. */
  on<K extends EventKey>(type: K, fn: Handler<K>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(fn);
    return () => {
      set!.delete(fn);
    };
  }

  once<K extends EventKey>(type: K, fn: Handler<K>): () => void {
    const off = this.on(type, (p) => {
      off();
      fn(p);
    });
    return off;
  }

  emit<K extends EventKey>(type: K, payload: GameEvents[K]): void {
    const set = this.handlers.get(type);
    if (!set || set.size === 0) return;
    for (const fn of Array.from(set)) {
      try {
        fn(payload);
      } catch (err) {
        // One faulty listener must never break the others (or the frame).
        console.error(`[bus] handler for "${type}" threw`, err);
      }
    }
  }

  listenerCount(type: EventKey): number {
    return this.handlers.get(type)?.size ?? 0;
  }

  clear(): void {
    this.handlers.clear();
  }
}

/** The single game-wide bus. */
export const bus = new EventBus();

/** Collects unsubscribe functions so a system can drop all listeners in destroy(). */
export class Subscriptions {
  private offs: (() => void)[] = [];

  on<K extends EventKey>(type: K, fn: Handler<K>): this {
    this.offs.push(bus.on(type, fn));
    return this;
  }

  add(off: () => void): this {
    this.offs.push(off);
    return this;
  }

  dispose(): void {
    for (const off of this.offs) off();
    this.offs = [];
  }
}
